/**
 * Persiste metadatos del handoff opaque same-origin para el shell WMS.
 * Debe usar las mismas claves que front `tigerPortalRouteStorage` /
 * `persistAuthSessionData` — el handoff por LoginPortal no monta /m/:id.
 */

const TENANT_KEY = 'tiger_portal_tenant_key';
const ROUTE_VERSION_KEY = 'tiger_portal_route_version';
const GATEWAY_ORIGIN_KEY = 'tiger_portal_gateway_origin';
const HANDOFF_RES_KEY = 'wms_portal_handoff_res_v1';
const PORTAL_HANDOFF_TTL_MS = 24 * 60 * 60 * 1000;
const PENDING_PERFIL_SELECTION_KEY = 'wms_pending_perfil_selection';

/** Claves alineadas con shell `sessionUser*.js` / `persistAuthSessionData`. */
const SESSION_USER_ID_KEY = 'wms_userId';
const SESSION_USER_FULL_NAME_KEY = 'wms_userFullName';
const SESSION_USER_LEGAJO_KEY = 'wms_userLegajo';
const SESSION_USER_PERFIL_ID_KEY = 'wms_userPerfilId';
const SESSION_USER_PERFIL_LABEL_KEY = 'wms_userPerfilLabel';
const MOBILE_REFRESH_TOKEN_KEY = 'wms_mobile_refresh_token';

/**
 * Último tenant del handoff Tiger (sobrevive logout; no lo borra clearTigerPortalResumeArtifacts).
 * Sirve para rehidratar branding en la pantalla Gateway post-logout.
 */
export function getPersistedTigerTenantKey(stores = {}) {
  const local = stores.localStorage
    ?? (typeof window !== 'undefined' ? window.localStorage : null);
  const session = stores.sessionStorage
    ?? (typeof window !== 'undefined' ? window.sessionStorage : null);
  try {
    const fromLocal = String(local?.getItem(TENANT_KEY) || '').trim();
    if (fromLocal) return fromLocal;
  } catch {
    /* ignore */
  }
  try {
    return String(session?.getItem(TENANT_KEY) || '').trim();
  } catch {
    return '';
  }
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const IDENTITY_KEYS = [
  SESSION_USER_ID_KEY,
  SESSION_USER_FULL_NAME_KEY,
  SESSION_USER_LEGAJO_KEY,
  SESSION_USER_PERFIL_ID_KEY,
  SESSION_USER_PERFIL_LABEL_KEY,
];

function removeStoreItem(store, key) {
  try {
    store?.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Clear-then-write: parity shell clearUserIdentityStorage (local + session). */
function clearAuthIdentityStorage() {
  for (const key of IDENTITY_KEYS) {
    removeStoreItem(typeof localStorage !== 'undefined' ? localStorage : null, key);
    removeStoreItem(typeof sessionStorage !== 'undefined' ? sessionStorage : null, key);
  }
}

function setIdentityItem(key, value) {
  const v = String(value ?? '').trim();
  if (!v) return;
  try {
    localStorage.setItem(key, v);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(key, v);
  } catch {
    /* ignore */
  }
}

function clearMobileRefreshToken() {
  removeStoreItem(typeof localStorage !== 'undefined' ? localStorage : null, MOBILE_REFRESH_TOKEN_KEY);
}

function parseUserId(data) {
  const root = data?.data ?? data ?? {};
  const user = root.user ?? root.usuario ?? root;
  const candidates = [
    root.NUSUARIO,
    user?.NUSUARIO,
    root.auth_user_id,
    user?.auth_user_id,
    root.id,
    root.Id,
    user?.id,
    user?.Id,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isInteger(n) && n > 0 && String(c).trim() === String(n)) return String(n);
  }
  for (const c of candidates) {
    const s = String(c ?? '').trim();
    if (UUID_RE.test(s)) return s.toLowerCase();
  }
  for (const c of candidates) {
    const s = String(c ?? '').trim();
    if (s) return s;
  }
  return null;
}

function parseUserFullName(data) {
  return data?.userFullName ?? data?.data?.userFullName ?? null;
}

function parseUserLegajo(data) {
  const root = data?.data ?? data ?? {};
  const user = root.user ?? root.usuario ?? root;
  const raw =
    root.legajo
    ?? root.LegNume
    ?? user?.legajo
    ?? user?.LegNume;
  const legajo = String(raw ?? '').trim();
  return legajo || null;
}

function parseActivePerfil(data) {
  const root = data?.data ?? data ?? {};
  const activeRow = root.perfil_activo ?? root.perfilActivo ?? null;
  if (!activeRow || typeof activeRow !== 'object') {
    return { id: null, label: null };
  }
  const id = String(
    activeRow.id_perfil ?? activeRow.idPerfil ?? activeRow.perfil?.id_perfil ?? ''
  ).trim();
  const label = String(
    activeRow.nombre ?? activeRow.label ?? activeRow.perfil?.nombre ?? ''
  ).trim();
  return { id: id || null, label: label || null };
}

/**
 * Identidad legible antes del remount del shell (parity con persistAuthSessionData).
 * @param {unknown} data
 */
export function persistAuthIdentityFromHandoff(data) {
  if (!data || typeof data !== 'object') return;
  const userId = parseUserId(data);
  const fullName = parseUserFullName(data);
  const legajo = parseUserLegajo(data);
  const { id: perfilId, label: perfilLabel } = parseActivePerfil(data);

  clearAuthIdentityStorage();

  if (userId) setIdentityItem(SESSION_USER_ID_KEY, userId);
  if (fullName) setIdentityItem(SESSION_USER_FULL_NAME_KEY, fullName);
  if (legajo) setIdentityItem(SESSION_USER_LEGAJO_KEY, legajo);
  if (perfilId) setIdentityItem(SESSION_USER_PERFIL_ID_KEY, perfilId);
  if (perfilLabel) setIdentityItem(SESSION_USER_PERFIL_LABEL_KEY, perfilLabel);

  const root = data.data ?? data;
  const refresh = String(
    data.refresh_token
    ?? data.refreshToken
    ?? root.refresh_token
    ?? root.refreshToken
    ?? root.session?.refresh_token
    ?? root.session?.refreshToken
    ?? ''
  ).trim();

  // Biometría sin refresh no debe dejar refresh de otro usuario.
  clearMobileRefreshToken();
  if (refresh) {
    try {
      localStorage.setItem(MOBILE_REFRESH_TOKEN_KEY, refresh);
    } catch {
      /* ignore */
    }
  }
}

/**
 * @param {unknown} portal
 */
export function persistTigerPortalRouteFromHandoff(portal) {
  if (!portal || typeof portal !== 'object') return;
  const tenantKey = String(
    portal.tiger_tenant_key || portal.tenant_key || portal.cliente_codigo || ''
  ).trim();
  const routeVersion = String(
    portal.tiger_route_version || portal.route_version || ''
  ).trim();
  let gatewayOrigin = String(portal.gateway_origin || '').trim().replace(/\/+$/, '');
  if (!gatewayOrigin && typeof window !== 'undefined') {
    gatewayOrigin = window.location.origin;
  }
  try {
    if (tenantKey) {
      sessionStorage.setItem(TENANT_KEY, tenantKey);
      localStorage.setItem(TENANT_KEY, tenantKey);
    }
    if (routeVersion) {
      sessionStorage.setItem(ROUTE_VERSION_KEY, routeVersion);
      localStorage.setItem(ROUTE_VERSION_KEY, routeVersion);
    }
    if (gatewayOrigin && /^https?:\/\//i.test(gatewayOrigin)) {
      sessionStorage.setItem(GATEWAY_ORIGIN_KEY, gatewayOrigin);
      localStorage.setItem(GATEWAY_ORIGIN_KEY, gatewayOrigin);
    }
  } catch {
    /* quota / private mode */
  }
}

/**
 * Guarda el envelope de handoff para que /login?step=profile tenga altRes
 * (setPerfilActivo no devuelve userFullName / refresh).
 * Persiste en sessionStorage + localStorage (sobrevive kill WebView Tiger).
 * @param {unknown} data
 */
export function persistPortalHandoffRes(data) {
  if (!data || typeof data !== 'object') return;
  const raw = JSON.stringify({
    savedAt: Date.now(),
    data,
  });
  try {
    sessionStorage.setItem(HANDOFF_RES_KEY, raw);
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(HANDOFF_RES_KEY, raw);
  } catch {
    /* ignore */
  }
}

export function clearPortalHandoffRes(stores = {}) {
  const local = stores.localStorage
    ?? (typeof window !== 'undefined' ? window.localStorage : null);
  const session = stores.sessionStorage
    ?? (typeof window !== 'undefined' ? window.sessionStorage : null);
  removeStoreItem(session, HANDOFF_RES_KEY);
  removeStoreItem(local, HANDOFF_RES_KEY);
}

/**
 * ¿Hay que mostrar picker antes de montar el shell? (parity shell pendingPerfilSelection)
 * @param {unknown} data
 */
function handoffNeedsProfileSelection(data) {
  if (!data || typeof data !== 'object') return false;
  const root = data.data && typeof data.data === 'object' ? data.data : data;
  if (root.requiere_seleccion_perfil === true || root.requiere_seleccion_perfil === 1) {
    return true;
  }
  if (String(root.modo_acceso || '').toLowerCase() === 'pendiente_seleccion_perfil') {
    return true;
  }
  const total = Number(root.total_perfiles_disponibles);
  if (Number.isFinite(total) && total > 1) return true;
  if (Array.isArray(root.perfiles_disponibles) && root.perfiles_disponibles.length > 1) {
    return true;
  }
  if (root.perfil_propio && root.perfil_prestado) return true;
  return false;
}

/**
 * @param {unknown} data — payload de portal-handoff-opaque
 */
export function persistSameOriginHandoffSession(data) {
  if (!data || typeof data !== 'object') return;
  persistTigerPortalRouteFromHandoff(data.portal);
  persistAuthIdentityFromHandoff(data);
  persistPortalHandoffRes(data);
  try {
    if (handoffNeedsProfileSelection(data)) {
      sessionStorage.setItem(PENDING_PERFIL_SELECTION_KEY, '1');
    } else {
      sessionStorage.removeItem(PENDING_PERFIL_SELECTION_KEY);
    }
  } catch {
    /* ignore */
  }
}
